<template>
  <div
    ref="contentContainer"
    :class="['content-container']"
    :style="{
      '--font-multiplier': String(fontMultiplier),
    }"
    :dir="isRightToLeft ? 'rtl' : 'ltr'"
  >
    <GeneralMessageChatText
      v-for="(messageDetails, index) in contentTarget"
      ref="refsText"
      :key="getKey(messageDetails)"
      :class="['translated-text', getContentClass(messageDetails, index)]"
      :dir="isRightToLeft ? 'rtl' : 'ltr'"
      :style="styleParagraph"
      :is-last-sub-id="index === contentTarget.length - 1"
      :message-details
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, CSSProperties } from 'vue';

import GeneralMessageChatText from '@/components/domains/chat/chat-general/GeneralMessageChatText.vue';
import { useMessageTextCommon } from '@/composables/domains/chat/useMessageTextCommon';
import { useChatPreferenceStore } from '@/stores/chatPreference';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { MessageText } from '@/types/socket';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { ij_isLastIsOther } from '@/utils/injectionKeys.js';

const userStore = useUserStore();
const chatPreferenceStore = useChatPreferenceStore();

const props = withDefaults(
  defineProps<{
    contentTarget: MessageText[];
    isInterpreting: boolean;
    languageCodeSet: Set<LanguageCode>;
  }>(),
  {}
);

const isLastIsOther = inject(ij_isLastIsOther, ref(false));

const contentContainer = ref<HTMLElement>();
const refsText = ref<HTMLElement>();

const { getKey } = useMessageTextCommon({
  contentContainer,
});

const styleParagraph = computed((): CSSProperties => {
  return {
    wordBreak: isEveryLanguageSpaced.value ? 'keep-all' : 'break-all',
  };
});

const isEveryLanguageSpaced = computed(() => {
  return [...props.languageCodeSet].every((code) => !isLanguageNoSpace(code));
});

// 오른쪽 정렬 언어인지 반환
const isRightToLeft = computed(() => {
  const listLangAlignRight: LanguageCode[] = ['ar']; // 아랍어,
  return listLangAlignRight.some((lang) => props.languageCodeSet.has(lang));
});

/// 글자 크기 배율
const fontMultiplier = computed(() => {
  return chatPreferenceStore.screenPreference.textSizeMultiplier ?? 1;
});

// 문장별 클래스
const getContentClass = (content: MessageText, index: number): object => {
  const isLastPart = index === props.contentTarget.length - 1;

  return {
    'highlight-translated': isLastPart ? (content.highlight ?? false) : isLastIsOther.value,

    'highlight-translated-fadeout': !isLastIsOther.value || !!content.fadeout,

    'bigger-text': userStore.userPreferences.isActiveTextLarger,
  };
};
</script>

<style scoped>
.content-container {
  overflow: hidden;
  min-height: 1px;
  height: auto;
  padding: 0;
  transition:
    height 0.7s ease,
    min-height 0.7s ease;
  text-align: start;
}

/* 번역본 - 텍스트 */
.translated-text {
  font-size: calc(var(--font-size-h2) * var(--font-multiplier, 1));
  font-weight: var(--font-weight-medium);
  line-height: 140%;
  color: var(--color-gray-10);
  opacity: 0.5;
  border-radius: 4px;
  unicode-bidi: isolate;
}

/* 통역본 - 큰 텍스트 */
.bigger-text {
  font-size: calc(var(--font-size-h1) * var(--font-multiplier, 1));
  line-height: 140%;
}

/* 통역본 - 통역/입력중인 텍스트 */
.highlight-translated {
  display: inline;
  color: var(--color-gray-10);
  animation: none;
  opacity: 0.5;
}

/* 통역본 - 통역 완료된 텍스트 */
.highlight-translated-fadeout {
  display: inline;
  color: var(--color-gray-10);
  animation: fadeIn 3s ease-in-out forwards;
}

@keyframes fadeIn {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
